import * as React from 'react';
import { useState, useEffect, useRef, Fragment } from "react";
import { Menu, Transition } from '@headlessui/react';
import { useAppNavigation } from "../../hooks/useAppNavigation";
import SubPages from "../../pages/subpages/SubPagesEnum";
import apiService from '../../services/ApiService';
import { useAppStore } from '../../shared/stores/useAppStore';
import TailwindButton from '../Tailwind/TailwindButton';
import TailwindModal from '../Tailwind/TailwindModal';
import { cn } from '../../lib/utils';

export interface UsersOrganization {
    user_id: number;
    email: string;
    right_to_read: boolean;
    right_to_write: boolean;
    right_to_share: boolean;
    right_to_duplicate: boolean;
}

export interface UsersSharedModel {
    user_id: number;
    right_to_read: boolean;
    right_to_write: boolean;
    right_to_share: boolean;
    right_to_duplicate: boolean;
}

export interface UsersEmail {
    email: string;
    right_to_read: boolean;
    right_to_write: boolean;
    right_to_share: boolean;
    right_to_duplicate: boolean;
}

const PlanLimitations = ({ model }) => {
    return (
        <div>
            <h2 className="text-xl font-bold text-center mb-4">
                Plan Limitations
            </h2>
            <div className="flex flex-row items-center mb-2">
                <p className="mr-2">
                    <b>Is active :</b>
                </p>
                {
                    model.modelRulesDeducted.is_active ?
                    <span className="material-symbols-outlined text-green-600">check_circle</span> :
                    <span className="material-symbols-outlined text-red-600">cancel</span>
                }
            </div>
            {
                !model.modelRulesDeducted.is_active && model.modelRulesDeducted.inactive_reason ?
                <div className="flex flex-row items-center mb-2">
                    <p className="mr-2">
                        <b>Inactive Reason :</b>
                    </p>
                    <p>
                        {model.modelRulesDeducted.inactive_reason}
                    </p>
                </div> : null
            }
            <div className="flex flex-row items-center mb-2">
                <p className="mr-2">
                    <b>Share inside organization :</b>
                </p>
                {
                    model.modelRulesDeducted.can_share_inside_organization ?
                    <span className="material-symbols-outlined text-green-600">check_circle</span> :
                    <span className="material-symbols-outlined text-red-600">cancel</span>
                }
            </div>
            <div className="flex flex-row items-center mb-2">
                <p className="mr-2">
                    <b>Share outside organization :</b>
                </p>
                {
                    model.modelRulesDeducted.can_share_outside_organization ?
                    <span className="material-symbols-outlined text-green-600">check_circle</span> :
                    <span className="material-symbols-outlined text-red-600">cancel</span>
                }
            </div>
            <div className="flex flex-row items-center mb-2">
                <p className="mr-2">
                    <b>Number of scenarios per model :</b>
                </p>
                <p>
                    {model.modelRulesDeducted.number_scenario_max_per_model}
                </p>
            </div>
            <div className="flex flex-row items-center mb-2">
                <p className="mr-2">
                    <b>Number of sensitivities per scenario :</b>
                </p>
                <p>
                    {model.modelRulesDeducted.number_sensitivities_max_per_scenario}
                </p>
            </div>
            <div className="flex flex-row items-center mb-2">
                <p className="mr-2">
                    <b>Business plan operational access :</b>
                </p>
                {
                    model.modelRulesDeducted.bpo_access ?
                    <span className="material-symbols-outlined text-green-600">check_circle</span> :
                    <span className="material-symbols-outlined text-red-600">cancel</span>
                }
            </div>
        </div>
    )
}


const SharingInfoBanner = () => (
    <div className="flex items-start gap-2 p-3 rounded-lg text-sm bg-blue-50 text-blue-800 border border-blue-200">
        <span className="material-symbols-outlined text-[18px] shrink-0 mt-0.5">info</span>
        <p>
            Permissions are subject to each recipient's commercial plan.
            Users without an active licence will have read-only access regardless of the permissions granted here.
            Guest users cannot edit shared models.
        </p>
    </div>
);

const ShareWithOrganization = ({ reloadState, handleCloseShare, model, userRights = undefined as any }) => {

    const [rows, setRows] = useState([]);
    const [selectionModel, setSelectionModel] = useState<number[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [usersOrganization, setUsersOrganization] = useState([{
        user_id: 0,
        email: '',
        right_to_read: false,
        right_to_write: false,
        right_to_share: false,
        right_to_duplicate: false,
    } as UsersOrganization]);
    const [displayError, setDisplayError] = useState({
        message: '',
        variant: ''
    });

    const handleAgreeOrganization = () => {
        if (usersOrganization[0].user_id != 0 && usersOrganization[0].email != '') {
            apiService.shareUsersOrganization(model.id, usersOrganization).then(async (response) => {
                if (response) {
                    if (response.ok) {
                        model.isShared = true;
                        setDisplayError({
                            message: "User(s) successfully invited in sharing",
                            variant: 'success'
                        });
                        reloadState();
                    } else {
                        const elem = await response.json();
                        setDisplayError({
                            message: elem.detail,
                            variant: 'error'
                        });
                    }
                }
            });
        }
    };

    const handleCheckboxChange = (checked: boolean, rowId: number, field: string) => {
        setDisplayError({ message: '', variant: '' });
        const updatedRows = rows.map((row) => {
            if (row.id === rowId) {
                if (checked && field === 'share' && (!userRights || userRights.right_to_share))
                    return { ...row, read: true, write: true, share: true };
                if (checked && field === 'write' && (!userRights || userRights.right_to_write))
                    return { ...row, read: true, write: true };
                if (!checked && field === 'write' && row.share && (!userRights || userRights.right_to_write))
                    return { ...row, write: true };
                if (!checked && field === 'read' && row.write && (!userRights || userRights.right_to_read))
                    return { ...row, read: true };
                if (userRights && checked && field === 'duplicate' && !userRights.right_to_duplicate)
                    return { ...row, duplicate: false };
                return { ...row, [field]: checked };
            }
            return row;
        });

        const selectedRows = selectionModel.map(id => updatedRows.find(row => row.id === id));
        const newUsersOrganization = selectedRows.map(row => ({
            user_id: row.id,
            email: row.emailAddress,
            right_to_read: row.read,
            right_to_write: row.write,
            right_to_share: row.share,
            right_to_duplicate: row.duplicate
        }));
        setRows(updatedRows);
        if (selectedRows.length)
            setUsersOrganization(newUsersOrganization);
    };

    const handleSelectionChange = (newSelection: number[]) => {
        setDisplayError({ message: '', variant: '' });
        setSelectionModel(newSelection);
        const selectedRows = newSelection.map(id => rows.find(row => row.id === id));
        if (selectedRows.length) {
            const newUsersOrganization = selectedRows.map(row => ({
                user_id: row.id,
                email: row.emailAddress,
                right_to_read: row.read,
                right_to_write: row.write,
                right_to_share: row.share,
                right_to_duplicate: row.duplicate
            }));
            setUsersOrganization(newUsersOrganization);
        } else {
            setUsersOrganization([{
                user_id: 0,
                email: '',
                right_to_read: false,
                right_to_write: false,
                right_to_share: false,
                right_to_duplicate: false,
            }]);
        }

        const resetRows = rows.map((row) => {
            if (!newSelection.includes(row.id)) {
                return { ...row, read: false, write: false, share: false, duplicate: false };
            }
            return row;
        });
        setRows(resetRows);
    };

    useEffect(() => {
        apiService.getUsersOrganizationSharedModel(model.id).then(async (response) => {
            if (response) {
                if (response.ok) {
                    const elem = await response.json();
                    const newRows = elem.map(item => ({
                        id: item.id,
                        firstName: item.first_name,
                        lastName: item.last_name,
                        emailAddress: item.email,
                        read: false,
                        write: false,
                        share: false,
                        duplicate: false
                    }));
                    setRows(newRows);
                }
            }
        });
    }, []);

    const filteredRows = rows.filter(row => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        const fullName = `${row.firstName || ''} ${row.lastName || ''}`.toLowerCase();
        return fullName.includes(q) || row.emailAddress.toLowerCase().includes(q);
    });

    const permissionFields = ['read', 'write', 'share', 'duplicate'];

    return (
        <div className="w-full">
            <div className="space-y-4">
                <SharingInfoBanner />
                <div className="relative">
                    <span className="material-symbols-outlined text-[18px] text-slate-400 absolute left-3 top-1/2 -translate-y-1/2">search</span>
                    <input
                        type="text"
                        placeholder="Search by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                </div>

                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="max-h-[450px] overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                                <tr>
                                    <th className="w-10 px-3 py-2.5"></th>
                                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</th>
                                    {permissionFields.map(field => (
                                        <th key={field} className="px-2 py-2.5 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider w-[70px]">
                                            {field.charAt(0).toUpperCase() + field.slice(1)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-400">
                                            No users found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredRows.map((row) => {
                                        const isSelected = selectionModel.includes(row.id);
                                        return (
                                            <tr key={row.id} className={cn("transition-colors", isSelected ? "bg-primary/5" : "hover:bg-slate-50")}>
                                                <td className="px-3 py-2 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => {
                                                            const newSel = isSelected
                                                                ? selectionModel.filter(id => id !== row.id)
                                                                : [...selectionModel, row.id];
                                                            handleSelectionChange(newSel);
                                                        }}
                                                        className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                                                    />
                                                </td>
                                                <td className="px-3 py-2 text-slate-700 font-medium whitespace-nowrap">{row.firstName} {row.lastName}</td>
                                                <td className="px-3 py-2 text-slate-500">{row.emailAddress}</td>
                                                {permissionFields.map(field => (
                                                    <td key={field} className="px-2 py-2 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={row[field]}
                                                            onChange={(e) => handleCheckboxChange(e.target.checked, row.id, field)}
                                                            disabled={!isSelected}
                                                            className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary disabled:opacity-30 cursor-pointer disabled:cursor-default"
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {displayError.message && (
                    <div className={cn(
                        "flex items-center gap-2 p-3 rounded-lg text-sm",
                        displayError.variant === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
                    )}>
                        <span className="material-symbols-outlined text-[18px] shrink-0">
                            {displayError.variant === 'success' ? 'check_circle' : 'error'}
                        </span>
                        {displayError.message}
                    </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                    <TailwindButton variant="secondary" onClick={handleCloseShare}>
                        Cancel
                    </TailwindButton>
                    <TailwindButton variant="dark" icon="person_add" onClick={handleAgreeOrganization} disabled={selectionModel.length === 0}>
                        Share
                    </TailwindButton>
                </div>
            </div>
        </div>
    );
}

/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////


const ShareWithEmail = ({ reloadState, handleCloseShare, model, userRights = undefined as any }) => {

    const [usersEmail, setUsersEmail] = useState([{
        email: '',
        right_to_read: false,
        right_to_write: false,
        right_to_share: false,
        right_to_duplicate: false,
    }]);
    const [errors, setErrors] = useState(['']);
    const [displayError, setDisplayError] = useState({
        message: '',
        variant: ''
    });

    const handleAgreeEmail = () => {
        const hasErrors = errors.some(error => error !== '');
        if (!hasErrors) {
            apiService.shareUsersEmail(model.id, usersEmail).then(async (response) => {
                if (response) {
                    if (response.ok) {
                        model.isShared = true;
                        setDisplayError({
                            message: "User(s) successfully invited in sharing",
                            variant: 'success'
                        });
                        reloadState();
                    } else {
                        const elem = await response.json();
                        setDisplayError({
                            message: elem.detail,
                            variant: 'error'
                        });
                    }
                }
            });
        }
    };

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(String(email).toLowerCase());
    };

    const addUsersEmail = () => {
        setUsersEmail([
            ...usersEmail,
            {
                email: '',
                right_to_read: false,
                right_to_write: false,
                right_to_share: false,
                right_to_duplicate: false
            } as UsersEmail
        ]);
        setErrors([...errors, '']);
    };

    const handleInputChange = (index, event) => {
        setDisplayError({ message: '', variant: '' });
        const newUsersEmail = [...usersEmail];
        newUsersEmail[index].email = event.target.value;
        setUsersEmail(newUsersEmail);

        const email = event.target.value;
        const newErrors = [...errors];
        if (!validateEmail(email)) {
            newErrors[index] = 'Invalid email address';
        } else {
            newErrors[index] = '';
        }
        setErrors(newErrors);
    };

    const handleCheckboxChange = (index: number, field: string) => (event) => {
        setDisplayError({ message: '', variant: '' });
        const newUsersEmail = [...usersEmail];
        newUsersEmail[index][field] = event.target.checked;
        if (event.target.checked == true && field == 'right_to_share' && (!userRights || userRights.right_to_share)) {
            newUsersEmail[index]['right_to_read'] = event.target.checked;
            newUsersEmail[index]['right_to_write'] = event.target.checked;
            newUsersEmail[index]['right_to_share'] = event.target.checked;
        }
        if (event.target.checked == true && field == 'right_to_write' && (!userRights || userRights.right_to_write)) {
            newUsersEmail[index]['right_to_read'] = event.target.checked;
            newUsersEmail[index]['right_to_write'] = event.target.checked;
        }
        if (event.target.checked == false && field == 'right_to_write' && usersEmail[index].right_to_share == true && (!userRights || userRights.right_to_write))
            newUsersEmail[index]['right_to_write'] = true;
        if (event.target.checked == false && field == 'right_to_read' && usersEmail[index].right_to_write == true && (!userRights || userRights.right_to_read))
            newUsersEmail[index]['right_to_read'] = true;
        if (userRights && event.target.checked == true && field == 'right_to_duplicate' && userRights.right_to_duplicate == false)
            newUsersEmail[index]['right_to_duplicate'] = false;

        setUsersEmail(newUsersEmail);
    };

    const removeUsersEmail = (index) => {
        if (usersEmail.length > 1) {
            const newUsersEmail = usersEmail.filter((_, i) => i !== index);
            const newErrors = errors.filter((_, i) => i !== index);
            setUsersEmail(newUsersEmail);
            setErrors(newErrors);
        }
    };

    const permissionFields = [
        { key: 'right_to_read', label: 'Read' },
        { key: 'right_to_write', label: 'Write' },
        { key: 'right_to_share', label: 'Share' },
        { key: 'right_to_duplicate', label: 'Duplicate' },
    ];

    return (
        <div className="w-full">
            <div className="space-y-4">
                <p className="text-sm text-slate-500 text-center">
                    Enter the emails of the users you want to share your model with.
                </p>
                <SharingInfoBanner />
                <div className="max-h-[60vh] overflow-y-auto space-y-3">
                    {usersEmail.map((userEmail, index) => (
                        <div key={index} className="border border-slate-200 rounded-lg p-4">
                            <div className="flex items-start gap-2">
                                <div className="flex-1">
                                    <label className="block text-xs font-medium text-slate-500 mb-1">
                                        Email {index + 1}
                                    </label>
                                    <input
                                        type="email"
                                        className={cn(
                                            "w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2",
                                            errors[index] ? 'border-red-300 focus:ring-red-200 focus:border-red-400' : 'border-slate-200 focus:ring-primary/20 focus:border-primary'
                                        )}
                                        value={userEmail.email}
                                        placeholder="user@example.com"
                                        onChange={(event) => handleInputChange(index, event)}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    />
                                    {errors[index] && (
                                        <p className="text-xs text-red-500 mt-1">{errors[index]}</p>
                                    )}
                                </div>
                                {usersEmail.length > 1 && (
                                    <button
                                        onClick={() => removeUsersEmail(index)}
                                        className="mt-6 p-1 rounded-md hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">close</span>
                                    </button>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-4 mt-3">
                                {permissionFields.map(({ key, label }) => (
                                    <label key={key} className="flex items-center gap-2 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={userEmail[key]}
                                            onChange={handleCheckboxChange(index, key)}
                                            className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                                        />
                                        <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors">{label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <button
                    onClick={addUsersEmail}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                >
                    <span className="material-symbols-outlined text-[18px]">add</span>
                    Add another email
                </button>

                {displayError.message && (
                    <div className={cn(
                        "flex items-center gap-2 p-3 rounded-lg text-sm",
                        displayError.variant === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
                    )}>
                        <span className="material-symbols-outlined text-[18px] shrink-0">
                            {displayError.variant === 'success' ? 'check_circle' : 'error'}
                        </span>
                        {displayError.message}
                    </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                    <TailwindButton variant="secondary" onClick={handleCloseShare}>
                        Cancel
                    </TailwindButton>
                    <TailwindButton variant="dark" icon="person_add" onClick={handleAgreeEmail} disabled={usersEmail.some(u => u.email === '') || errors.some(e => e !== '')}>
                        Share
                    </TailwindButton>
                </div>
            </div>
        </div>
    );
}

/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////

function ContentDeleteUser ({ setRows, rows, params, reloadState, model, setUsersSharedModel, handleCloseManageSharing })
{
    const[openDeleteUser, setOpenDeleteUser] = React.useState(false);

    const handleCloseDeleteUser = () => {
        setOpenDeleteUser(false);
    };

    const handleOpenDeleteUser = () => {
        setOpenDeleteUser(true);
    };

    const handleCloseDeleteUserAgree = () => {
        handleCloseDeleteUser();

        apiService.deleteUserSharing(model.id, params.id).then(async(response) => {
        if (response) {
            if (response.ok)
            {
                const filteredRows = rows.filter((item) => {
                    return (item.id !== params.id)
                });
                if (!filteredRows.length)
                {
                    model.isShared = false;
                    reloadState();
                    handleCloseManageSharing();
                }
                setRows(filteredRows)
                const newUsersSharedModel = filteredRows.map(row => ({
                    user_id: row.id,
                    email: row.emailAddress,
                    right_to_read: row.read,
                    right_to_write: row.write,
                    right_to_share: row.share,
                    right_to_duplicate: row.duplicate
                }));
                setUsersSharedModel(newUsersSharedModel);
            }
        }
        })
    };

    const handleCloseDeleteUserCancel = () => {
      handleCloseDeleteUser();
    };

    return(
        <div>
            <button
                onClick={handleOpenDeleteUser}
                className="p-1 rounded hover:bg-black/5 transition-colors"
            >
                <span className="material-symbols-outlined text-[20px] text-red-600">person_remove</span>
            </button>
            <TailwindModal
                isOpen={openDeleteUser}
                onClose={handleCloseDeleteUser}
                size="sm"
            >
                <div>
                    <h2 className="text-xl font-bold text-center mb-4">
                        Are you sure ?
                    </h2>
                    <p className="text-base text-center">
                        This will delete <b>{params.row.firstName + ' ' + params.row.lastName}</b> from the shared model.
                    </p>
                    <div className="mt-5 flex justify-center gap-3">
                        <button
                            onClick={handleCloseDeleteUserCancel}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-black/5 transition-colors"
                        >
                            <span className="material-symbols-outlined text-[20px]">close</span>
                            <span className="text-sm">Cancel</span>
                        </button>
                        <button
                            onClick={handleCloseDeleteUserAgree}
                            className="flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-black/5 transition-colors"
                        >
                            <span className="material-symbols-outlined text-[20px] text-red-600">person_remove</span>
                            <span className="text-sm text-red-600">Yes, Delete</span>
                        </button>
                    </div>
                </div>
            </TailwindModal>
        </div>
    );
}

const ManageSharing = ({ reloadState, handleCloseManageSharing, model, userRights = undefined as any }) => {

    const [rows, setRows] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [usersSharedModel, setUsersSharedModel] = useState([{
        user_id: 0,
        email: '',
        right_to_read: false,
        right_to_write: false,
        right_to_share: false,
        right_to_duplicate: false,
    } as UsersSharedModel]);
    const [displayError, setDisplayError] = useState({
        message: '',
        variant: ''
    });

    const handleAgreeManageSharing = () => {
        if (usersSharedModel[0].user_id != 0) {
            apiService.manageUsersSharing(model.id, usersSharedModel).then(async (response) => {
                if (response) {
                    if (response.ok) {
                        setDisplayError({
                            message: "Change(s) saved successfully",
                            variant: 'success'
                        });
                    } else {
                        const elem = await response.json();
                        setDisplayError({
                            message: elem.detail,
                            variant: 'error'
                        });
                    }
                }
            });
        }
    };

    const handleCheckboxChange = (checked: boolean, rowId: number, field: string) => {
        setDisplayError({ message: '', variant: '' });
        const updatedRows = rows.map((row) => {
            if (row.id === rowId) {
                if (checked && field === 'share' && (!userRights || userRights.right_to_share))
                    return { ...row, read: true, write: true, share: true };
                if (checked && field === 'write' && (!userRights || userRights.right_to_write))
                    return { ...row, read: true, write: true };
                if (!checked && field === 'write' && row.share && (!userRights || userRights.right_to_write))
                    return { ...row, write: true };
                if (!checked && field === 'read' && row.write && (!userRights || userRights.right_to_read))
                    return { ...row, read: true };
                if (userRights && checked && field === 'duplicate' && !userRights.right_to_duplicate)
                    return { ...row, duplicate: false };
                return { ...row, [field]: checked };
            }
            return row;
        });
        const newUsersSharedModel = updatedRows.map(row => ({
            user_id: row.id,
            email: row.emailAddress,
            organization: row.organization,
            sharer_name: row.name,
            right_to_read: row.read,
            right_to_write: row.write,
            right_to_share: row.share,
            right_to_duplicate: row.duplicate
        }));
        setRows(updatedRows);
        setUsersSharedModel(newUsersSharedModel);
    };

    useEffect(() => {
        apiService.getUsersManageSharing(model.id).then(async (response) => {
            if (response) {
                if (response.ok) {
                    const elem = await response.json();
                    const newRows = elem.map(item => ({
                        id: item.id,
                        firstName: item.first_name,
                        lastName: item.last_name,
                        emailAddress: item.email,
                        organization: item.organization,
                        sharerFirstName: item.sharer_first_name,
                        sharerLastName: item.sharer_last_name,
                        read: item.right_to_read,
                        write: item.right_to_write,
                        share: item.right_to_share,
                        duplicate: item.right_to_duplicate
                    }));
                    setRows(newRows);
                }
            }
        });
    }, []);

    const filteredRows = rows.filter(row => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        const fullName = `${row.firstName || ''} ${row.lastName || ''}`.toLowerCase();
        const sharerName = `${row.sharerFirstName || ''} ${row.sharerLastName || ''}`.toLowerCase();
        return fullName.includes(q) || row.emailAddress.toLowerCase().includes(q) || sharerName.includes(q) || (row.organization || '').toLowerCase().includes(q);
    });

    const permissionFields = ['read', 'write', 'share', 'duplicate'];

    return (
        <div className="w-full">
            <div className="space-y-4">
                <SharingInfoBanner />
                <div className="relative">
                    <span className="material-symbols-outlined text-[18px] text-slate-400 absolute left-3 top-1/2 -translate-y-1/2">search</span>
                    <input
                        type="text"
                        placeholder="Search by name, email, or organization..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                </div>

                <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="max-h-[450px] overflow-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                                <tr>
                                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</th>
                                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Sharer</th>
                                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Organization</th>
                                    {permissionFields.map(field => (
                                        <th key={field} className="px-2 py-2.5 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider w-[70px]">
                                            {field.charAt(0).toUpperCase() + field.slice(1)}
                                        </th>
                                    ))}
                                    <th className="w-10 px-2 py-2.5"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filteredRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={10} className="px-3 py-10 text-center text-sm text-slate-400">
                                            No users found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredRows.map((row) => {
                                        const isCurrentUser = userRights && userRights.user_id == row.id;
                                        return (
                                            <tr key={row.id} className={cn("transition-colors", isCurrentUser ? "bg-slate-50/50" : "hover:bg-slate-50")}>
                                                <td className="px-3 py-2 text-slate-700 font-medium whitespace-nowrap">
                                                    {row.firstName} {row.lastName}
                                                    {isCurrentUser && <span className="ml-1.5 text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">You</span>}
                                                </td>
                                                <td className="px-3 py-2 text-slate-500">{row.emailAddress}</td>
                                                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{row.sharerFirstName} {row.sharerLastName}</td>
                                                <td className="px-3 py-2 text-slate-500">{row.organization}</td>
                                                {permissionFields.map(field => (
                                                    <td key={field} className="px-2 py-2 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={row[field]}
                                                            onChange={(e) => handleCheckboxChange(e.target.checked, row.id, field)}
                                                            disabled={isCurrentUser}
                                                            className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary disabled:opacity-30 cursor-pointer disabled:cursor-default"
                                                        />
                                                    </td>
                                                ))}
                                                <td className="px-2 py-2 text-center">
                                                    {!isCurrentUser && (
                                                        <ContentDeleteUser
                                                            setRows={setRows}
                                                            rows={rows}
                                                            params={{ id: row.id, row: { firstName: row.firstName, lastName: row.lastName } }}
                                                            reloadState={reloadState}
                                                            model={model}
                                                            setUsersSharedModel={setUsersSharedModel}
                                                            handleCloseManageSharing={handleCloseManageSharing}
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {displayError.message && (
                    <div className={cn(
                        "flex items-center gap-2 p-3 rounded-lg text-sm",
                        displayError.variant === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
                    )}>
                        <span className="material-symbols-outlined text-[18px] shrink-0">
                            {displayError.variant === 'success' ? 'check_circle' : 'error'}
                        </span>
                        {displayError.message}
                    </div>
                )}

                <div className="flex justify-end gap-3 pt-2">
                    <TailwindButton variant="secondary" onClick={handleCloseManageSharing}>
                        Cancel
                    </TailwindButton>
                    <TailwindButton variant="dark" icon="check_circle" onClick={handleAgreeManageSharing}>
                        Save changes
                    </TailwindButton>
                </div>
            </div>
        </div>
    );
}

/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////

const InformationsSharedModel = ({ handleCloseInformations, model, userRights }) => {
    const [userSharer, setUserSharer] = useState({
        owner_name: '',
        sharer_name: '',
        organization_owner: '',
        organization_sharer: '',
    });

    useEffect(() => {
        apiService.getUserSharer(model.id).then(async response => {
            if (response) {
                if (response.ok) {
                    const elem = await response.json();
                    setUserSharer({
                        owner_name: elem.owner_first_name + ' ' + elem.owner_last_name,
                        sharer_name: elem.sharer_first_name + ' ' + elem.sharer_last_name,
                        organization_owner: elem.organization_owner,
                        organization_sharer: elem.organization_sharer,
                    });
                }
            }
        });
    }, []);

    const permissions = [
        { key: 'right_to_read', label: 'Read', icon: 'visibility' },
        { key: 'right_to_write', label: 'Write', icon: 'edit' },
        { key: 'right_to_share', label: 'Share', icon: 'person_add' },
        { key: 'right_to_duplicate', label: 'Duplicate', icon: 'content_copy' },
    ];

    return (
        <div>
            <h2 className="text-lg font-bold text-center text-slate-900 mb-2">
                Sharing Information
            </h2>
            <p className="text-sm text-center text-slate-500 mb-6">
                Shared with you by <span className="font-semibold text-slate-700">{userSharer.sharer_name}</span> from <span className="font-semibold text-slate-700">{userSharer.organization_sharer}</span>
            </p>

            <div className="border-t border-slate-200 pt-4">
                <p className="text-sm font-semibold text-slate-700 text-center mb-3">
                    Your permissions
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                    {permissions.map(({ key, label, icon }) => (
                        <div
                            key={key}
                            className={cn(
                                "flex items-center gap-2 px-3 py-2 rounded-lg border text-sm",
                                userRights[key]
                                    ? "bg-green-50 border-green-200 text-green-700"
                                    : "bg-slate-50 border-slate-200 text-slate-400"
                            )}
                        >
                            <span className="material-symbols-outlined text-[18px]">{icon}</span>
                            {label}
                            {userRights[key] ? (
                                <span className="material-symbols-outlined text-[16px] text-green-600">check</span>
                            ) : (
                                <span className="material-symbols-outlined text-[16px] text-slate-300">close</span>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex justify-center pt-6">
                <TailwindButton variant="secondary" onClick={handleCloseInformations}>
                    Close
                </TailwindButton>
            </div>
        </div>
    );
};

/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////


export default function ModelListItemOptionMenu({ model, reloadState, state, isSharedContext = false, userRights = undefined as any, stateMyModels = undefined as any, anchorEl = undefined as HTMLElement | null | undefined, onClose = undefined as (() => void) | undefined }) {
    const commercialPlan = useAppStore(s => s.commercialPlan);
    const navigateToPage = useAppNavigation();

    const rules = model.modelRulesDeducted;
    // Support both new props (anchorEl/onClose) and legacy model.anchorEl
    const effectiveAnchorEl = anchorEl !== undefined ? anchorEl : model.anchorEl;
    const menuOpen = Boolean(effectiveAnchorEl);
    const closeMenu = onClose || (() => { model.anchorEl = null; });
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
    const[openShare, setOpenShare] = useState(false);
    const[openManageSharing, setOpenManageSharing] = useState(false);
    const[openInformations, setOpenInformations] = useState(false);
    const[shareOrganization, setShareOrganization] = useState((!rules?.can_share_inside_organization && rules?.can_share_outside_organization) ? false : true);
    const[openPlanLimitations, setOpenPlanLimitations] = useState(false);
    const[duplicating, setDuplicating] = useState(false);

    useEffect(() => {
        if (menuOpen && effectiveAnchorEl) {
            const rect = effectiveAnchorEl.getBoundingClientRect();
            const menuWidth = 210;
            const menuHeight = 400; // approximate max
            setMenuPos({
                top: Math.min(rect.bottom + 4, window.innerHeight - menuHeight - 8),
                left: Math.min(rect.left, window.innerWidth - menuWidth - 8),
            });
        } else {
            setMenuPos(null);
        }
    }, [menuOpen]);

    const handleOpenShare = () => {
        setOpenShare(true);
    };

    const handleCloseShare = () => {
        setOpenShare(false);
    };

    const handleOpenManageSharing = () => {
        setOpenManageSharing(true);
    };

    const handleCloseManageSharing = () => {
        setOpenManageSharing(false);
    };

    const handleOpenInformations = () => {
        setOpenInformations(true);
    };

    const handleCloseInformations = () => {
        setOpenInformations(false);
    };

    const handleOpenPlanLimitations = () => {
        setOpenPlanLimitations(true);
    };

    const handleClosePlanLimitations = () => {
        setOpenPlanLimitations(false);
    };

    return (
        <div>
            {/* Duplicate loading backdrop */}
            {duplicating && (
                <div className="fixed inset-0 z-[100] bg-white/80 backdrop-blur-sm flex items-center justify-center">
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm font-medium text-slate-600">Duplicating model...</p>
                    </div>
                </div>
            )}
            <Menu as="div" className="relative inline-block">
                <Menu.Button className="hidden" />

                {menuPos && (
                <Transition
                    show={!!menuOpen}
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Menu.Items
                        ref={menuRef}
                        static
                        className="w-52 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 max-h-[80vh] overflow-y-auto"
                        style={{
                            position: 'fixed',
                            top: menuPos.top,
                            left: menuPos.left,
                        }}
                        onClick={(e) => {e.stopPropagation()}}
                    >
                        <div className="p-1">
                            {/* Open Financial - gated by is_active */}
                            <Menu.Item disabled={!model.modelRulesDeducted.is_active}>
                                {({ active }) => (
                                    <button
                                        disabled={!model.modelRulesDeducted.is_active}
                                        onClick={() => {
                                            closeMenu();
                                            if (isSharedContext) {
                                                if (userRights?.right_to_read) {
                                                    if (model.isNew) {
                                                        navigateToPage(SubPages.NewModel, model);
                                                    } else {
                                                        navigateToPage(SubPages.FinancialModelEditor, model);
                                                    }
                                                }
                                            } else {
                                                if (model.isNew) {
                                                    navigateToPage(SubPages.NewModel, model);
                                                } else {
                                                    navigateToPage(SubPages.FinancialModelEditor, model);
                                                }
                                            }
                                        }}
                                        className={cn(
                                            'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                            active && model.modelRulesDeducted.is_active && 'bg-black/5',
                                            !model.modelRulesDeducted.is_active && 'opacity-50 cursor-not-allowed'
                                        )}
                                    >
                                        <span className="material-symbols-outlined text-[20px] mr-2">monitoring</span>
                                        {!model.modelRulesDeducted.is_active ? 'Open Financial (Plan expired)' : 'Open Financial'}
                                    </button>
                                )}
                            </Menu.Item>
                            {/* Open Operational - gated by bpo_access AND is_active */}
                            <Menu.Item disabled={!model.modelRulesDeducted.bpo_access || !model.modelRulesDeducted.is_active}>
                                {({ active }) => (
                                    <button
                                        disabled={!model.modelRulesDeducted.bpo_access || !model.modelRulesDeducted.is_active}
                                        onClick={() => {
                                            closeMenu();
                                            if (!model.isNew)
                                            {
                                                navigateToPage(SubPages.OperationalModelEditor, model);
                                            }
                                        }}
                                        className={cn(
                                            'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                            active && model.modelRulesDeducted.bpo_access && model.modelRulesDeducted.is_active && 'bg-black/5',
                                            (!model.modelRulesDeducted.bpo_access || !model.modelRulesDeducted.is_active) && 'opacity-50 cursor-not-allowed'
                                        )}
                                    >
                                        <span className="material-symbols-outlined text-[20px] mr-2">analytics</span>
                                        {!model.modelRulesDeducted.is_active ? 'Open Operational (Plan expired)' : !model.modelRulesDeducted.bpo_access ? 'Open Operational (Upgrade required)' : 'Open Operational'}
                                    </button>
                                )}
                            </Menu.Item>
                            <hr className="my-1 border-slate-100" />
                            {/* Rename - only when NOT shared context */}
                            {
                                !isSharedContext ?
                                <Menu.Item disabled={!model.modelRulesDeducted.is_active}>
                                    {({ active }) => (
                                        <button
                                            disabled={!model.modelRulesDeducted.is_active}
                                            onClick={() => {
                                                model.editing = true;
                                                closeMenu();
                                                reloadState();
                                            }}
                                            className={cn(
                                                'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                                active && model.modelRulesDeducted.is_active && 'bg-black/5',
                                                !model.modelRulesDeducted.is_active && 'opacity-50 cursor-not-allowed'
                                            )}
                                        >
                                            <span className="material-symbols-outlined text-[20px] mr-2">edit</span>
                                            Rename
                                        </button>
                                    )}
                                </Menu.Item> : null
                            }
                            {/* Duplicate - different logic for owned vs shared */}
                            {
                                isSharedContext ?
                                (
                                    userRights?.right_to_duplicate && model.modelRulesDeducted?.can_duplicate ?
                                    <Menu.Item>
                                        {({ active }) => (
                                            <button
                                                onClick={() => {
                                                    closeMenu();
                                                    setDuplicating(true);
                                                    apiService.duplicateSharedModel(model.id).then(async(response) => {
                                                        if (response) {
                                                            const newModel = await response.json()

                                                            stateMyModels.models[newModel.id.toString()] = {
                                                                id: newModel.id,
                                                                name: newModel.name,
                                                                folderId: newModel.folder_id,
                                                                editing: true,
                                                                deleteModalOpen: false,
                                                                modelScenarios: newModel.model_scenarios,
                                                                isNew: newModel.is_new,
                                                                type: 'MODEL',
                                                                modelRulesDeducted: newModel.model_rules_deducted,
                                                            }
                                                            const indexToInsert = stateMyModels.folders[newModel.folder_id.toString()].modelChildrensIds.indexOf(model.id)
                                                            stateMyModels.folders[newModel.folder_id.toString()].modelChildrensIds.splice(indexToInsert + 1, 0, newModel.id)

                                                            reloadState();
                                                        }
                                                        setDuplicating(false);
                                                    })
                                                }}
                                                className={cn(
                                                    'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                                    active && 'bg-black/5'
                                                )}
                                            >
                                                <span className="material-symbols-outlined text-[20px] mr-2">content_copy</span>
                                                Duplicate
                                            </button>
                                        )}
                                    </Menu.Item> : null
                                )
                                :
                                <Menu.Item disabled={!model.modelRulesDeducted?.can_duplicate}>
                                    {({ active }) => (
                                        <button
                                            disabled={!model.modelRulesDeducted?.can_duplicate}
                                            onClick={() => {
                                                closeMenu();
                                                setDuplicating(true);
                                                apiService.duplicateModel(model.id).then(async(response) => {
                                                    if (response) {
                                                        if (response.ok)
                                                        {
                                                            const newModel = await response.json()

                                                            state.models[newModel.id.toString()] = {
                                                                id: newModel.id,
                                                                name: newModel.name,
                                                                folderId: newModel.folder_id,
                                                                editing: true,
                                                                deleteModalOpen: false,
                                                                modelScenarios: newModel.model_scenarios,
                                                                isNew: newModel.is_new,
                                                                type: 'MODEL',
                                                                modelRulesDeducted: newModel.model_rules_deducted,
                                                            }
                                                            const indexToInsert = state.folders[newModel.folder_id.toString()].modelChildrensIds.indexOf(model.id)
                                                            state.folders[newModel.folder_id.toString()].modelChildrensIds.splice(indexToInsert + 1, 0, newModel.id)

                                                            reloadState();
                                                        }
                                                    }
                                                    setDuplicating(false);
                                                })
                                            }}
                                            className={cn(
                                                'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                                active && model.modelRulesDeducted?.can_duplicate && 'bg-black/5',
                                                !model.modelRulesDeducted?.can_duplicate && 'opacity-50 cursor-not-allowed'
                                            )}
                                        >
                                            <span className="material-symbols-outlined text-[20px] mr-2">content_copy</span>
                                            Duplicate
                                        </button>
                                    )}
                                </Menu.Item>
                            }
                            {/* Share - owned: show if share rules allow; shared: show if userRights.right_to_share AND share rules allow */}
                            {
                                isSharedContext ?
                                (
                                    userRights?.right_to_share && (model.modelRulesDeducted.can_share_inside_organization || model.modelRulesDeducted.can_share_outside_organization) ?
                                    <Menu.Item>
                                        {({ active }) => (
                                            <button
                                                onClick={() => {
                                                    closeMenu();
                                                    handleOpenShare();
                                                    reloadState();
                                                }}
                                                className={cn(
                                                    'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                                    active && 'bg-black/5'
                                                )}
                                            >
                                                <span className="material-symbols-outlined text-[20px] mr-2">person_add</span>
                                                Share
                                            </button>
                                        )}
                                    </Menu.Item> : null
                                )
                                :
                                <Menu.Item disabled={!model.modelRulesDeducted.can_share_inside_organization && !model.modelRulesDeducted.can_share_outside_organization}>
                                    {({ active }) => (
                                        <button
                                            disabled={!model.modelRulesDeducted.can_share_inside_organization && !model.modelRulesDeducted.can_share_outside_organization}
                                            onClick={() => {
                                                closeMenu();
                                                reloadState();
                                                handleOpenShare();
                                            }}
                                            className={cn(
                                                'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                                active && (model.modelRulesDeducted.can_share_inside_organization || model.modelRulesDeducted.can_share_outside_organization) && 'bg-black/5',
                                                (!model.modelRulesDeducted.can_share_inside_organization && !model.modelRulesDeducted.can_share_outside_organization) && 'opacity-50 cursor-not-allowed'
                                            )}
                                        >
                                            <span className="material-symbols-outlined text-[20px] mr-2">person_add</span>
                                            Share
                                        </button>
                                    )}
                                </Menu.Item>
                            }
                            {/* Manage sharing - owned: show if model.isShared; shared: show if userRights.right_to_share */}
                            {
                                isSharedContext ?
                                (
                                    userRights?.right_to_share ?
                                    <Menu.Item>
                                        {({ active }) => (
                                            <button
                                                onClick={() => {
                                                    closeMenu();
                                                    reloadState();
                                                    handleOpenManageSharing();
                                                }}
                                                className={cn(
                                                    'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                                    active && 'bg-black/5'
                                                )}
                                            >
                                                <span className="material-symbols-outlined text-[20px] mr-2">manage_accounts</span>
                                                Manage sharing
                                            </button>
                                        )}
                                    </Menu.Item> : null
                                )
                                :
                                (
                                    model.isShared ?
                                    <Menu.Item>
                                        {({ active }) => (
                                            <button
                                                onClick={() => {
                                                    closeMenu();
                                                    reloadState();
                                                    handleOpenManageSharing();
                                                }}
                                                className={cn(
                                                    'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                                    active && 'bg-black/5'
                                                )}
                                            >
                                                <span className="material-symbols-outlined text-[20px] mr-2">manage_accounts</span>
                                                Manage sharing
                                            </button>
                                        )}
                                    </Menu.Item> : null
                                )
                            }
                            {/* Share Informations - only when shared context */}
                            {
                                isSharedContext ?
                                <Menu.Item>
                                    {({ active }) => (
                                        <button
                                            onClick={() => {
                                                closeMenu();
                                                handleOpenInformations();
                                                reloadState();
                                            }}
                                            className={cn(
                                                'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                                active && 'bg-black/5'
                                            )}
                                        >
                                            <span className="material-symbols-outlined text-[20px] mr-2">info</span>
                                            Share Informations
                                        </button>
                                    )}
                                </Menu.Item> : null
                            }
                            <hr className="my-1 border-slate-100" />
                            {/* Plan Limitations - always shown */}
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={() => {
                                            closeMenu();
                                            handleOpenPlanLimitations();
                                        }}
                                        className={cn(
                                            'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                            active && 'bg-black/5'
                                        )}
                                    >
                                        <span className="material-symbols-outlined text-[20px] mr-2">info</span>
                                        Plan Limitations
                                    </button>
                                )}
                            </Menu.Item>
                            <hr className="my-1 border-slate-100" />
                            {/* Stop sharing - only when NOT shared context and model.isShared */}
                            {
                                !isSharedContext && model.isShared ?
                                <Menu.Item>
                                    {({ active }) => (
                                        <button
                                            onClick={() => {
                                                apiService.stopSharingModel(model.id).then(async(response) => {
                                                    if (response) {
                                                        if (response.ok)
                                                        {
                                                            closeMenu();
                                                            model.isShared = false;
                                                            reloadState();
                                                        }
                                                    }
                                                })
                                            }}
                                            className={cn(
                                                'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                                active && 'bg-black/5'
                                            )}
                                        >
                                            <span className="material-symbols-outlined text-[20px] mr-2 text-red-600">block</span>
                                            <span className="text-red-600">Stop sharing</span>
                                        </button>
                                    )}
                                </Menu.Item> : null
                            }
                            {/* Delete - only when NOT shared context */}
                            {
                                !isSharedContext ?
                                <Menu.Item disabled={!model.modelRulesDeducted.can_delete}>
                                    {({ active }) => (
                                        <button
                                            disabled={!model.modelRulesDeducted.can_delete}
                                            onClick={() => {
                                                model.deleteModalOpen = true;
                                                closeMenu();
                                                reloadState();
                                            }}
                                            className={cn(
                                                'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                                active && model.modelRulesDeducted.can_delete && 'bg-black/5',
                                                !model.modelRulesDeducted.can_delete && 'opacity-50 cursor-not-allowed'
                                            )}
                                        >
                                            <span className="material-symbols-outlined text-[20px] mr-2 text-red-600">delete_forever</span>
                                            <span className="text-red-600">Delete</span>
                                        </button>
                                    )}
                                </Menu.Item> : null
                            }
                            {/* Leave sharing - only when shared context */}
                            {
                                isSharedContext ?
                                <Menu.Item>
                                    {({ active }) => (
                                        <button
                                            onClick={() => {
                                                model.deleteModalOpen = true;
                                                closeMenu();
                                                reloadState();
                                            }}
                                            className={cn(
                                                'flex items-center w-full px-3 py-2 text-sm text-left rounded-md transition-colors',
                                                active && 'bg-black/5'
                                            )}
                                        >
                                            <span className="material-symbols-outlined text-[20px] mr-2 text-red-600">logout</span>
                                            <span className="text-red-600">Leave sharing</span>
                                        </button>
                                    )}
                                </Menu.Item> : null
                            }
                        </div>
                    </Menu.Items>
                </Transition>
                )}

                {menuOpen && (
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => {
                            closeMenu();
                            reloadState();
                        }}
                    />
                )}
            </Menu>

            <TailwindModal
                isOpen={openShare}
                onClose={handleCloseShare}
                size="3xl"
                showCloseButton={false}
            >
                <div onClick={(e) => {e.stopPropagation()}}>
                    <div className="flex justify-center mb-6">
                        <div className="inline-flex rounded-lg border border-slate-100 p-1">
                            <button
                                disabled={!model.modelRulesDeducted.can_share_inside_organization}
                                onClick={() => setShareOrganization(true)}
                                className={cn(
                                    "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                                    shareOrganization ? "bg-black text-white" : "text-slate-600 hover:text-slate-900",
                                    !model.modelRulesDeducted.can_share_inside_organization && "opacity-50 cursor-not-allowed"
                                )}
                            >
                                Organization
                            </button>
                            <button
                                disabled={!model.modelRulesDeducted.can_share_outside_organization}
                                onClick={() => setShareOrganization(false)}
                                className={cn(
                                    "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                                    !shareOrganization ? "bg-black text-white" : "text-slate-600 hover:text-slate-900",
                                    !model.modelRulesDeducted.can_share_outside_organization && "opacity-50 cursor-not-allowed"
                                )}
                            >
                                Mail
                            </button>
                        </div>
                    </div>
                    {
                        shareOrganization ?
                        <ShareWithOrganization reloadState={reloadState} handleCloseShare={handleCloseShare} model={model} userRights={userRights} /> :
                        <ShareWithEmail reloadState={reloadState} handleCloseShare={handleCloseShare} model={model} userRights={userRights} />
                    }
                </div>
            </TailwindModal>

            <TailwindModal
                isOpen={openManageSharing}
                onClose={handleCloseManageSharing}
                size="4xl"
                showCloseButton={true}
            >
                <div onClick={(e) => {e.stopPropagation()}}>
                    <ManageSharing reloadState={reloadState} handleCloseManageSharing={handleCloseManageSharing} model={model} userRights={userRights} />
                </div>
            </TailwindModal>

            {isSharedContext && (
                <TailwindModal
                    isOpen={openInformations}
                    onClose={handleCloseInformations}
                    size="lg"
                    showCloseButton={true}
                >
                    <div onClick={(e) => {e.stopPropagation()}}>
                        <InformationsSharedModel handleCloseInformations={handleCloseInformations} model={model} userRights={userRights} />
                    </div>
                </TailwindModal>
            )}

            <TailwindModal
                isOpen={openPlanLimitations}
                onClose={handleClosePlanLimitations}
                size="lg"
                showCloseButton={true}
            >
                <div onClick={(e) => {e.stopPropagation()}}>
                    <PlanLimitations model={model}/>
                </div>
            </TailwindModal>
        </div>
    )
}

export { PlanLimitations }
